import teamsByLeague from '../data/teamsByLeague';
import {roConsensus} from "../actions/options";

export const baseUrl = process.env.REACT_APP_BASE_URL;
export const mobileBreakpoint = 900;

export const sortByFc = (playerA, playerB) => {
    if(playerA.fcRank === 'N/A' && playerB.fcRank !== 'N/A') return 1;
    if(playerA.fcRank !== 'N/A' && playerB.fcRank === 'N/A') return -1;
    return playerA.fcRank - playerB.fcRank;
};

export const getFormattedPlayer = (playerInfo, pickedByTeam, roundPicked, totalPickNumber, isMyTurn) => ({
    ...playerInfo,
    pickedBy: {...pickedByTeam, isUserTeam:isMyTurn},
    numberPicked: totalPickNumber,
    roundPicked,
});

export const getTeamLogo = (code) => {
    const fileName = `${getTeamName[code]}.svg`;
    return `${process.env.PUBLIC_URL}/team-logos/${code}/${fileName}`;
};

export const getNationalityFlag = (nat) => `https://nhlentrydraft.com/wp-content/themes/fc-hockey/img/flags/${nat}.svg`;

export const getAvailablePlayers = (allPlayers, selectedPlayers) =>
    allPlayers.filter((player) => !selectedPlayers.some((selected)=> selected.id === player.id));

export const getFilteredPlayers = (players, selectedPlayers, filters) => {
    const filtered = players
           .filter((player) => !player.position || player.position.indexOf(filters['positions']) !== -1)
           .filter((player) => !player.league || player.league.indexOf(filters['leagues']) !== -1)
           .filter((player) => `${player.firstName} ${player.lastName}`.toLowerCase().includes(filters['search'].toLowerCase().trim()) ||
                               `${player.lastName} ${player.firstName}`.toLowerCase().includes(filters['search'].toLowerCase().trim()));
    return filters['availability'] === 'Available' ? getAvailablePlayers(filtered, selectedPlayers) : filtered;
}

export const getPlayerLink = (player) => `${baseUrl}/players/${player.firstName.toLowerCase()}-${player.lastName.toLowerCase()}/`;

export const randomIntFromInterval = (min, max) => {
    return Math.floor(Math.random() * (max - min + 1) + min);
}

export const getRandomPlayerInRange = (players, rankings) => {
    let ranges;

    if (rankings === roConsensus) {
        // Consensus Ranking Logic
        ranges = [
            1, 3,    // Tier 1: 1-3
            4, 9,    // Tier 2: 4-9
            10, 15,  // Tier 3: 10-15
            16, 25,  // Tier 4: 16-25
            26, 40,  // Tier 5: 26-40
            41, 55,  // Tier 6: 41-55
            56, 64,  // Tier 7: 56-64
            65, 999 // Anything beyond tier 7
        ];
    } else {
        // FC Hockey Ranking Logic
        ranges = [
            1, 3,    // Tier 1: 1-3
            4, 4,    // Tier 2: 4
            5, 8,    // Tier 3: 5-8
            9, 15,   // Tier 4: 9-15
            16, 22,  // Tier 5: 16-22
            23, 35,  // Tier 6: 23-35
            36, 50,  // Tier 7: 36-50
            51, 80,  // Tier 8: 51-80
            81, 110, // Tier 9: 81-110
            111, 140, // Tier 10: 111-140
            141, 190, // Tier 11: 141-190
            191, 224, // Tier 12: 191-224
            225, 300, // Tier 13: 225-300
            301, 999  // Anything beyond tier 13
        ];
    }

    const rangeStart = ranges.findIndex((x, idx) => players[0].fcRank >= x && players[0].fcRank < ranges[idx + 1]);
    let intervalLength = 1;
    for(let i = 1 ; i < players.length ; i++){
        if(players[i].fcRank >= ranges[rangeStart] && players[i].fcRank < ranges[rangeStart + 1]) intervalLength++;
        else break;
    }
    const randomIndex = randomIntFromInterval(0, intervalLength - 1);
    return players[randomIndex];
};

/**
 * Function to determine the current round based on the pick number
 *
 * @param roundPickRanges
 * @param currentPickNumber
 * @returns {number|null}
 */
export const getCurrentRound = (roundPickRanges, currentPickNumber) => {
    for (let i = 0; i < roundPickRanges.length; i++) {
        const { start, end } = roundPickRanges[i];
        if (currentPickNumber >= start && currentPickNumber <= end) {
            return i + 1; // Adding 1 to match round numbers (rounds are 1-indexed)
        }
    }

    return null; // Return null if pick number doesn't fall within any range
}

function ordinal_suffix_of(i) {
    var j = i % 10,
        k = i % 100;
    if (j === 1 && k !== 11) {
        return i + "st";
    }
    if (j === 2 && k !== 12) {
        return i + "nd";
    }
    if (j === 3 && k !== 13) {
        return i + "rd";
    }
    return i + "th";
}

export const pad = (stringNumber) => {
	if(parseInt(stringNumber) <= 9) return `0${stringNumber}`;
	return stringNumber;
};

export const formatDraftResult = (player) => {
    let result = player.draftResult;
    if(result && result !== '/' && result !== 'PENDING'){
        result = result.split('/');
        return `${ordinal_suffix_of(result[0])} Round (No.${result[1]})`; 
    }
    return 'Pending';
}

export const parseHeight = (height) => {
    let parts = height.split('\'');
    return `${parts[0]}'${Math.round(parseInt(parts[1]))}”`;
}

export const capitalize = (string) => string.charAt(0).toUpperCase() + string.substr(1).toLowerCase();

export const toSentenceCase = (string) => string.split(' ').map((s) => capitalize(s)).join(' ');

export const getTeamsByLeague = (league) => league ? teamsByLeague[league].map((t) => toSentenceCase(t)) : [];

export const getTeamName = {
    "ANA": 'anaheim-ducks',
    "ARI": 'arizona-coyotes',
    "ATL": 'atlanta-thrashers',
    "BOS": 'boston-bruins',
    "BUF": 'buffalo-sabres',
    "CAR": 'carolina-hurricanes',
    "CBJ": 'columbus-blue-jackets',
    "CGY": 'calgary-flames',
    "CHI": 'chicago-blackhawks',
    "COL": 'colorado-avalanche',
    "DAL": 'dallas-stars',
    "DET": 'detroit-red-wings',
    "EDM": 'edmonton-oilers',
    "FLA": 'florida-panthers',
    "LAK": 'los-angeles-kings',
    "MIN": 'minnesota-wild',
    "MON": 'montreal-canadiens',
    "NJD": 'new-jersey-devils',
    "NSH": 'nashville-predators',
    "NYI": 'new-york-islanders',
    "NYR": 'new-york-rangers',
    "OTT": 'ottawa-senators',
    "PHI": 'philadelphia-flyers',
    "PHX": 'phoenix-coyotes',
    "PIT": 'pittsburgh-penguins',
    "SJS": 'san-jose-sharks',
    "SEA": 'seattle-kraken',
    "STL": 'st-louis-blues',
    "TBL": 'tampa-bay-lightning',
    "TML": 'toronto-maple-leafs',
    "VAN": 'vancouver-canucks',
    "VGK": 'vegas-golden-knights',
    "WAS": 'washington-capitals',
    "WIN": 'winnipeg-jets',
    "UTA": 'utah',
};

export const getLeagues = () => `AJHL
ALLSVENSKAN
AUSTRIA
AUSTRIA-2
AUSTRIA-JR
BCHL
BELARUS
BELARUS-JR
BELARUS-JR.2
CAHS
CCHL
CJHL
CZECH
CZECH-2
CZECH-U18
CZECH-U20
CZECH-U20.2
DEL
DENMARK
DENMARK-2
DIVISION I
DNL
EIHL
FRANCE
GERMANY-2
GERMANY-3
GHL
GOJHL
J18 ELIT
J20 ELIT
JR A SM-liiga
JR B SM-sarja
KAZAKHSTAN
KHL
LATVIA
LIIGA
MESTIS
MHL
MIDGET
MJHL
MWEHL
MWJHL
NAHL
NCAA
NLA
NLB
NMHL
NOJHL
NORWAY
NTDP
OHL
OJHL
QMJHL
RUSSIA-3
SHL
SIJHL
SJHL
SLOVAKIA
SLOVAKIA-2
SLOVAKIA-U20
J20 Nationell
U20 ELIT
US-TIER3
USHL
USHS
VHL
WHL`.split('\n');